import type { TAction } from '@hozana/redux/types'
import { call, put, race, take } from '@hozana/sagas'

import type { TAllAction, TAllActionType } from 'config/types'
import { TConnectDetails, redirect } from 'general/functions'
import { POPINS } from 'general/managers/popins/constants'
import type { TPopinsOptions } from 'general/managers/popins/types'

import { closePopin, openPopin } from './actions'
import { AppActionTypes } from './constants'

/*
 * UTILS
 */

/**
 * Open a popin and wait for the "successActionType" message
 * If the popin is closed without triggering the result, return false
 *
 * @param popinName - Name of the popin (see config/constants.js file)
 * @param popinOptions - Options passed to the popin
 * @param successActionType - Action type which will trigger a success
 */
export function* runPopin<
  PN extends keyof TPopinsOptions = keyof TPopinsOptions,
  AT extends TAllActionType = TAllActionType,
>(popinName: PN, popinOptions: TPopinsOptions[PN], successActionType: AT): Generator<any, TAllAction<AT>> {
  yield* put(openPopin(popinName, popinOptions))

  const { success } = yield* race({
    success: take<TAllAction<AT>>(successActionType),
    failure: take(
      (action: TAction) => action.type === AppActionTypes.CLOSE_POPIN && (!action.name || action.name === popinName),
    ),
  })

  if (success) {
    // Make sure we close the popin
    yield* put(closePopin(popinName))

    // Return the success data
    return success
  }

  return null
}

export function* runConfirm(title: string, message?: string) {
  return yield* runPopin(POPINS.ConfirmationPopin, { title, message }, AppActionTypes.CONFIRM)
}

export function* runRedirect(options?: TConnectDetails) {
  return yield* call(redirect, options)
}
